import type { LocationState } from "./useLocation";
import { accuracyLabel } from "./useLocation";

type Props = {
  state: LocationState;
  onRetry: () => void;
};

/**
 * Hard gate shown before any streaming UI when location isn't available.
 * The only way past this screen is to grant high-accuracy geolocation.
 */
export default function LocationGate({ state, onRetry }: Props) {
  const isWaiting = state.status === "idle" || state.status === "prompting" || state.status === "locating";
  const isFailure = state.status === "denied" || state.status === "unavailable" || state.status === "timeout";

  return (
    <div className="loc-gate" role="dialog" aria-labelledby="loc-gate-title">
      <div className="loc-gate__card">
        <div className="loc-gate__pulse"><span /><span /><span /></div>

        <h1 id="loc-gate-title">Location is required to broadcast</h1>

        <p className="loc-gate__lede">
          Aruba Tourist Channel Live attaches verified location metadata to each mobile broadcast so
          the newsroom can confirm where live footage is coming from. <strong>Sharing your
          location is mandatory.</strong>
        </p>

        {isWaiting && (
          <>
            <p className="loc-gate__status">
              {state.status === "prompting" && "Please allow location access in your browser…"}
              {state.status === "locating" && "Locating you — keep the screen on, hold still if possible…"}
              {state.status === "idle" && "Click the button below to start."}
            </p>
            <button type="button" className="primary" onClick={onRetry}>
              {state.status === "idle" ? "Share my location" : "Try again"}
            </button>
          </>
        )}

        {isFailure && (
          <>
            <p className="loc-gate__err">
              <strong>
                {state.status === "denied" && "Location permission was denied."}
                {state.status === "unavailable" && "Your device can't determine your location."}
                {state.status === "timeout" && "Location request timed out."}
              </strong>
              <br />
              {state.error}
            </p>

            <details className="loc-gate__how">
              <summary>How do I enable location?</summary>
              <ul>
                <li><strong>iPhone / iPad:</strong> Settings → Privacy & Security → Location Services → Safari Websites → Allow. Then refresh this page.</li>
                <li><strong>Android (Chrome):</strong> Tap the lock icon in the address bar → Permissions → Location → Allow. Then refresh.</li>
                <li><strong>Desktop:</strong> Click the lock icon left of the URL → Site settings → Location → Allow. Then refresh.</li>
                <li>If you're indoors, move near a window for a faster GPS fix.</li>
              </ul>
            </details>

            <button type="button" className="primary danger" onClick={onRetry}>
              Try again
            </button>
          </>
        )}

        {state.current && (
          <div className="loc-gate__fix">
            <div>
              <strong>Got it.</strong> {accuracyLabel(state.current.accuracyM).label}
            </div>
            <div className="muted small">
              {state.current.lat.toFixed(6)}, {state.current.lon.toFixed(6)}
            </div>
          </div>
        )}

        <p className="loc-gate__legal muted small">
          Your location is sent to the broadcast operator and saved with the recording for
          editorial verification. It is not shown to the public player.
        </p>
      </div>
    </div>
  );
}
